import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { LuPlus } from "react-icons/lu";
import PropertyRequestCard from "../components/propertyRequest/PropertyRequestCard";
import ConfirmDialog from "../components/propertyRequest/ConfirmDialog";
import ReceivedProposals from "../components/propertyRequest/ReceivedProposals";
import { formatDate } from "../components/propertyRequest/labels";
import { propertyRequestApi } from "../Services/propertyRequestService";

const TABS = [["active", "tabActive"], ["closed", "tabClosed"], ["expired", "tabExpired"]];

// "Mes recherches" (personal space). Proposals are only counted here; their details come later.
export default function MyPropertyRequests() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const status = TABS.some(([key]) => key === params.get("status")) ? params.get("status") : "active";
  const [data, setData] = useState({ items: [], total: 0, page: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [confirm, setConfirm] = useState(null);
  const [openProposals, setOpenProposals] = useState(null);
  const [busy, setBusy] = useState(false);
  const version = useRef(0);

  const load = useCallback(async (page = 0) => {
    const request = ++version.current;
    setLoading(true); setError("");
    try {
      const result = await propertyRequestApi.mine(status, page, 20);
      if (request !== version.current) return;
      setData((previous) => ({ items: page === 0 ? result.items : [...previous.items, ...result.items], total: result.total, page }));
    } catch (e) {
      if (request === version.current) setError(e.message);
    } finally {
      if (request === version.current) setLoading(false);
    }
  }, [status]);

  useEffect(() => { setData({ items: [], total: 0, page: 0 }); load(0); }, [load]);

  // A new proposal (SignalR) or a reconnection: refetch the pages already shown, never count locally.
  const shownPages = useRef(0);
  shownPages.current = data.page;
  const refresh = useCallback(async () => {
    const request = ++version.current;
    try {
      const pages = [];
      for (let page = 0; page <= shownPages.current; page++) pages.push(await propertyRequestApi.mine(status, page, 20));
      if (request !== version.current) return;
      setData({ items: pages.flatMap((p) => p.items), total: pages[pages.length - 1].total, page: pages.length - 1 });
    } catch {
      // Offline or server error: keep the current list.
    } finally {
      if (request === version.current) setLoading(false);
    }
  }, [status]);

  useEffect(() => {
    window.addEventListener("propertyRequestProposal", refresh);
    window.addEventListener("messagesResumed", refresh);
    return () => {
      window.removeEventListener("propertyRequestProposal", refresh);
      window.removeEventListener("messagesResumed", refresh);
    };
  }, [refresh]);

  const act = async () => {
    const { kind, request } = confirm;
    setBusy(true);
    try {
      if (kind === "close") await propertyRequestApi.close(request.id);
      else await propertyRequestApi.remove(request.id);
      setData((d) => ({ ...d, items: d.items.filter((x) => x.id !== request.id), total: Math.max(0, d.total - 1) }));
      toast.success(t(kind === "close" ? "propertyRequest.mine.closed" : "propertyRequest.mine.deleted"));
      setConfirm(null);
    } catch (e) {
      toast.error(e.message);
      setConfirm(null);
    } finally {
      setBusy(false);
    }
  };

  const dates = (r) => {
    const published = t("propertyRequest.mine.published", { date: formatDate(r.createdAt, i18n.language) });
    const end = r.status === "Closed" && r.closedAt ? t("propertyRequest.mine.closedOn", { date: formatDate(r.closedAt, i18n.language) })
      : r.status === "Expired" ? t("propertyRequest.mine.expiredOn", { date: formatDate(r.expiresAt, i18n.language) })
      : t("propertyRequest.mine.expires", { date: formatDate(r.expiresAt, i18n.language) });
    return `${published} · ${end}`;
  };

  return (
    <main className="lina-property-request mx-auto w-full max-w-3xl px-4 py-6 sm:px-6 sm:py-10">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-3xl font-black text-slate-900 sm:text-4xl">{t("propertyRequest.mine.title")}</h1>
        <Link to="/property-request/new" className="lina-action-primary h-11"><LuPlus aria-hidden="true" />{t("propertyRequest.mine.new")}</Link>
      </div>

      <div role="tablist" aria-label={t("propertyRequest.mine.title")} className="mt-5 flex gap-1 overflow-x-auto border-b border-slate-200">
        {TABS.map(([key, label]) => (
          <button key={key} type="button" role="tab" aria-selected={status === key}
            onClick={() => setParams(key === "active" ? {} : { status: key }, { replace: true })}
            className={`min-h-[44px] shrink-0 border-b-2 px-3 text-sm font-semibold ${status === key ? "border-green-700 text-green-800" : "border-transparent text-slate-600 hover:text-slate-900"}`}>
            {t(`propertyRequest.mine.${label}`)}
          </button>
        ))}
      </div>

      <div role="tabpanel" className="mt-5 space-y-4">
        {error && (
          <div role="alert" className="flex flex-wrap items-center justify-between gap-2 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-800">
            <span>{error}</span>
            <button type="button" className="lina-action" onClick={() => load(0)}>{t("propertyRequest.mine.retry")}</button>
          </div>
        )}
        {!loading && !error && data.items.length === 0 && <p className="text-slate-600">{t("propertyRequest.mine.empty")}</p>}
        {data.items.map((r) => (
          <PropertyRequestCard key={r.id} request={r} showStatus dates={dates(r)}>
            {r.proposalCount > 0 && (
              <div className="flex flex-wrap items-center gap-2 px-4 pb-3 sm:px-5">
                <button type="button" aria-expanded={openProposals === r.id} onClick={() => setOpenProposals(openProposals === r.id ? null : r.id)}
                  className="min-h-[44px] text-sm font-semibold text-green-800 underline">
                  {openProposals === r.id ? t("propertyRequest.received.hide") : t("propertyRequest.received.show", { count: r.proposalCount })}
                </button>
                {r.newProposalCount > 0 && <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-bold text-amber-900">{t("propertyRequest.received.newCount", { count: r.newProposalCount })}</span>}
              </div>
            )}
            {openProposals === r.id && (
              // The server marks the opened proposals as seen: the local counter follows.
              <ReceivedProposals requestId={r.id} onLoaded={() => setData((d) => ({ ...d, items: d.items.map((x) => (x.id === r.id ? { ...x, newProposalCount: 0 } : x)) }))} />
            )}
            <div className="grid grid-flow-col auto-cols-fr border-t border-slate-100 text-sm font-semibold">
              {r.status === "Active" && <button type="button" className="min-h-[48px] text-green-800 hover:bg-green-50" onClick={() => navigate(`/property-request/${r.id}/edit`)}>{t("propertyRequest.mine.edit")}</button>}
              {r.status === "Active" && <button type="button" className="min-h-[48px] border-s border-slate-100 text-slate-800 hover:bg-slate-50" onClick={() => setConfirm({ kind: "close", request: r })}>{t("propertyRequest.mine.close")}</button>}
              <button type="button" className="min-h-[48px] border-s border-slate-100 text-red-700 hover:bg-red-50 first:border-s-0" onClick={() => setConfirm({ kind: "delete", request: r })}>{t("propertyRequest.mine.delete")}</button>
            </div>
          </PropertyRequestCard>
        ))}
        {loading && <p role="status" className="text-slate-600">{t("propertyRequest.mine.loading")}</p>}
        {!loading && data.items.length < data.total && (
          <button type="button" className="lina-action h-11 w-full" onClick={() => load(data.page + 1)}>{t("propertyRequest.mine.loadMore")}</button>
        )}
      </div>

      {confirm && (
        <ConfirmDialog
          title={t(confirm.kind === "close" ? "propertyRequest.mine.closeQuestion" : "propertyRequest.mine.deleteQuestion")}
          note={t(confirm.kind === "close" ? "propertyRequest.mine.closeNote" : "propertyRequest.mine.deleteNote")}
          confirmLabel={t(confirm.kind === "close" ? "propertyRequest.mine.close" : "propertyRequest.mine.delete")}
          danger={confirm.kind === "delete"} busy={busy} onConfirm={act} onCancel={() => setConfirm(null)} />
      )}
    </main>
  );
}
